import { Injectable } from '@angular/core';
import { Observable, defer, of, forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

/**
 * Configuration options for IndexedDB cache
 */
export interface IndexedDBCacheConfig {
    dbName: string;
    version?: number;
    storeName?: string;
}

/**
 * Cache entry structure with metadata
 */
export interface CacheEntry<T = any> {
    key: string;
    data: T;
    timestamp: number;
    expiresAt?: number;
    tags?: string[];
    metadata?: Record<string, any>;
}

/**
 * Options for storing cache entries
 */
export interface CacheStoreOptions {
    ttl?: number; // Time to live in milliseconds (relative to now)
    expiresAt?: number; // Absolute expiration timestamp (milliseconds); takes precedence over ttl
    tags?: string[];
    metadata?: Record<string, any>;
}

/**
 * Options for invalidating cache entries
 */
export interface InvalidateOptions {
    keys?: string[];
    tags?: string[];
    olderThan?: number; // Timestamp
    pattern?: RegExp;
    all?: boolean;
}

/**
 * Service for managing persistent cache using IndexedDB
 * Provides structured data storage with expiration and invalidation capabilities
 * All methods return Observables for reactive programming
 */
@Injectable({
    providedIn: 'root'
})
export class IndexedDBCacheService {
    private db: IDBDatabase | null = null;
    private dbName: string = 'app-cache';
    private storeName: string = 'cache-store';
    private version: number = 1;

    constructor() { }

    /**
     * Initialize the IndexedDB database
     * Opens (creating if necessary) the configured database and preserves any
     * previously persisted data so the cache survives across browser sessions.
     * Returns the database instance for use in piped operations.
     */
    initialize(config?: IndexedDBCacheConfig): Observable<IDBDatabase> {
        if (this.db) {
            return of(this.db);
        }

        return defer(() => {
            if (config) {
                this.dbName = config.dbName;
                this.version = config.version || 1;
                this.storeName = config.storeName || 'cache-store';
            }

            return new Observable<IDBDatabase>(observer => {
                this.openDatabase(observer);
            });
        });
    }

    /**
     * Open the IndexedDB database
     */
    private openDatabase(observer: any): void {
        const request = indexedDB.open(this.dbName, this.version);

        request.onerror = () => {
            observer.error(new Error(`Failed to open IndexedDB: ${request.error}`));
        };

        request.onsuccess = () => {
            this.db = request.result;
            observer.next(this.db);
            observer.complete();
        };

        request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
            const db = (event.target as IDBOpenDBRequest).result;

            // Create object store if it doesn't exist
            if (!db.objectStoreNames.contains(this.storeName)) {
                const objectStore = db.createObjectStore(this.storeName, { keyPath: 'key' });

                // Create indexes for efficient querying
                objectStore.createIndex('timestamp', 'timestamp', { unique: false });
                objectStore.createIndex('expiresAt', 'expiresAt', { unique: false });
                objectStore.createIndex('tags', 'tags', { unique: false, multiEntry: true });
            }
        };
    }

    /**
     * Ensure database is initialized
     * Returns the database instance for use in piped operations
     */
    private ensureInitialized(): Observable<IDBDatabase> {
        if (this.db) {
            return of(this.db);
        }
        return this.initialize();
    }

    /**
     * Store data in cache
     */
    set<T>(key: string, data: T, options?: CacheStoreOptions): Observable<void> {
        return this.ensureInitialized().pipe(
            switchMap((db) => {
                const entry: CacheEntry<T> = {
                    key,
                    data,
                    timestamp: Date.now(),
                    tags: options?.tags,
                    metadata: options?.metadata
                };

                if (options?.expiresAt) {
                    entry.expiresAt = options.expiresAt;
                } else if (options?.ttl) {
                    entry.expiresAt = Date.now() + options.ttl;
                }

                return new Observable<void>(observer => {
                    const transaction = db.transaction([this.storeName], 'readwrite');
                    const store = transaction.objectStore(this.storeName);
                    const request = store.put(entry);

                    request.onsuccess = () => {
                        observer.next();
                        observer.complete();
                    };
                    request.onerror = () => {
                        observer.error(new Error(`Failed to store cache entry: ${request.error}`));
                    };
                });
            })
        );
    }

    /**
     * Store multiple entries in a single transaction.
     * More efficient than calling set() repeatedly when persisting a batch of items.
     */
    setMany<T>(items: Array<{ key: string; data: T; options?: CacheStoreOptions }>): Observable<void> {
        if (!items || !items.length) {
            return of(undefined);
        }

        return this.ensureInitialized().pipe(
            switchMap((db) => new Observable<void>(observer => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);
                const now = Date.now();

                items.forEach(({ key, data, options }) => {
                    const entry: CacheEntry<T> = {
                        key,
                        data,
                        timestamp: now,
                        tags: options?.tags,
                        metadata: options?.metadata
                    };

                    if (options?.expiresAt) {
                        entry.expiresAt = options.expiresAt;
                    } else if (options?.ttl) {
                        entry.expiresAt = now + options.ttl;
                    }

                    store.put(entry);
                });

                transaction.oncomplete = () => {
                    observer.next();
                    observer.complete();
                };
                transaction.onerror = () => {
                    observer.error(new Error(`Failed to store cache entries: ${transaction.error}`));
                };
            }))
        );
    }

    /**
     * Retrieve all non-expired entries associated with a tag.
     * Uses the multiEntry 'tags' index for efficient lookups without a full scan.
     */
    getByTag<T>(tag: string): Observable<CacheEntry<T>[]> {
        return this.ensureInitialized().pipe(
            switchMap((db) => new Observable<CacheEntry<T>[]>(observer => {
                const transaction = db.transaction([this.storeName], 'readonly');
                const store = transaction.objectStore(this.storeName);
                const index = store.index('tags');
                const request = index.getAll(tag);

                request.onsuccess = () => {
                    const entries: CacheEntry<T>[] = request.result || [];
                    const now = Date.now();

                    // Filter out (and purge) expired entries
                    const validEntries = entries.filter(entry => {
                        if (entry.expiresAt && entry.expiresAt < now) {
                            this.delete(entry.key).subscribe({
                                error: (err) => console.error(err)
                            });

                            return false;
                        }

                        return true;
                    });

                    observer.next(validEntries);
                    observer.complete();
                };

                request.onerror = () => {
                    observer.error(new Error(`Failed to retrieve cache entries by tag: ${request.error}`));
                };
            }))
        );
    }

    /**
     * Retrieve data from cache
     */
    get<T>(key: string): Observable<T | null> {
        return this.ensureInitialized().pipe(
            switchMap((db) => new Observable<T | null>(observer => {
                const transaction = db.transaction([this.storeName], 'readonly');
                const store = transaction.objectStore(this.storeName);
                const request = store.get(key);

                request.onsuccess = () => {
                    const entry: CacheEntry<T> | undefined = request.result;

                    if (!entry) {
                        observer.next(null);
                        observer.complete();
                        return;
                    }

                    // Check if entry has expired
                    if (entry.expiresAt && entry.expiresAt < Date.now()) {
                        // Delete expired entry
                        this.delete(key).subscribe({
                            error: (err) => console.error(err)
                        });
                        observer.next(null);
                        observer.complete();
                        return;
                    }

                    observer.next(entry.data);
                    observer.complete();
                };

                request.onerror = () => {
                    observer.error(new Error(`Failed to retrieve cache entry: ${request.error}`));
                };
            }))
        );
    }

    /**
     * Get cache entry with metadata
     */
    getEntry<T>(key: string): Observable<CacheEntry<T> | null> {
        return this.ensureInitialized().pipe(
            switchMap((db) => new Observable<CacheEntry<T> | null>(observer => {
                const transaction = db.transaction([this.storeName], 'readonly');
                const store = transaction.objectStore(this.storeName);
                const request = store.get(key);

                request.onsuccess = () => {
                    const entry: CacheEntry<T> | undefined = request.result;

                    if (!entry) {
                        observer.next(null);
                        observer.complete();
                        return;
                    }

                    // Check if entry has expired
                    if (entry.expiresAt && entry.expiresAt < Date.now()) {
                        this.delete(key).subscribe({
                            error: (err) => console.error(err)
                        });
                        observer.next(null);
                        observer.complete();
                        return;
                    }

                    observer.next(entry);
                    observer.complete();
                };

                request.onerror = () => {
                    observer.error(new Error(`Failed to retrieve cache entry: ${request.error}`));
                };
            }))
        );
    }

    /**
     * Check if a key exists in cache and is not expired
     */
    has(key: string): Observable<boolean> {
        return this.get(key).pipe(
            map(data => data !== null)
        );
    }

    /**
     * Delete a specific cache entry
     */
    delete(key: string): Observable<void> {
        return this.ensureInitialized().pipe(
            switchMap((db) => new Observable<void>(observer => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);
                const request = store.delete(key);

                request.onsuccess = () => {
                    observer.next();
                    observer.complete();
                };
                request.onerror = () => {
                    observer.error(new Error(`Failed to delete cache entry: ${request.error}`));
                };
            }))
        );
    }

    /**
     * Get all keys in the cache
     */
    keys(): Observable<string[]> {
        return this.ensureInitialized().pipe(
            switchMap((db) => new Observable<string[]>(observer => {
                const transaction = db.transaction([this.storeName], 'readonly');
                const store = transaction.objectStore(this.storeName);
                const request = store.getAllKeys();

                request.onsuccess = () => {
                    observer.next(request.result as string[]);
                    observer.complete();
                };
                request.onerror = () => {
                    observer.error(new Error(`Failed to retrieve cache keys: ${request.error}`));
                };
            }))
        );
    }

    /**
     * Get all cache entries
     */
    getAll<T>(): Observable<CacheEntry<T>[]> {
        return this.ensureInitialized().pipe(
            switchMap((db) => new Observable<CacheEntry<T>[]>(observer => {
                const transaction = db.transaction([this.storeName], 'readonly');
                const store = transaction.objectStore(this.storeName);
                const request = store.getAll();

                request.onsuccess = () => {
                    const entries: CacheEntry<T>[] = request.result;
                    const now = Date.now();

                    // Filter out expired entries
                    const validEntries = entries.filter(entry => {
                        if (entry.expiresAt && entry.expiresAt < now) {
                            this.delete(entry.key).subscribe({
                                error: (err) => console.error(err)
                            });
                            return false;
                        }
                        return true;
                    });

                    observer.next(validEntries);
                    observer.complete();
                };

                request.onerror = () => {
                    observer.error(new Error(`Failed to retrieve all cache entries: ${request.error}`));
                };
            }))
        );
    }

    /**
     * Invalidate cache entries based on various criteria
     */
    invalidate(options: InvalidateOptions): Observable<number> {
        return this.ensureInitialized().pipe(
            switchMap(() => {
                // Clear all cache
                if (options.all) {
                    return this.clear().pipe(
                        switchMap(() => this.count())
                    );
                }

                const deleteOperations: Observable<void>[] = [];

                // Delete specific keys
                if (options.keys && options.keys.length > 0) {
                    options.keys.forEach(key => {
                        deleteOperations.push(this.delete(key));
                    });
                }

                // Delete by tags, age, or pattern
                const needsFullScan = options.tags || options.olderThan || options.pattern;

                if (needsFullScan) {
                    return this.getAll().pipe(
                        switchMap(entries => {
                            entries.forEach(entry => {
                                let shouldDelete = false;

                                // Check tags
                                if (options.tags && entry.tags) {
                                    shouldDelete = entry.tags.some(tag => options.tags!.includes(tag));
                                }

                                // Check age
                                if (!shouldDelete && options.olderThan) {
                                    shouldDelete = entry.timestamp < options.olderThan;
                                }

                                // Check pattern
                                if (!shouldDelete && options.pattern) {
                                    shouldDelete = options.pattern.test(entry.key);
                                }

                                if (shouldDelete) {
                                    deleteOperations.push(this.delete(entry.key));
                                }
                            });

                            if (deleteOperations.length === 0) {
                                return of(0);
                            }

                            return forkJoin(deleteOperations).pipe(
                                map(() => deleteOperations.length)
                            );
                        })
                    );
                }

                // Only key-based deletion
                if (deleteOperations.length === 0) {
                    return of(0);
                }

                return forkJoin(deleteOperations).pipe(
                    map(() => deleteOperations.length)
                );
            })
        );
    }

    /**
     * Clear all expired entries
     */
    clearExpired(): Observable<number> {
        return this.ensureInitialized().pipe(
            switchMap((db) => new Observable<number>(observer => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);
                const index = store.index('expiresAt');
                const now = Date.now();

                // Get all entries with expiresAt less than now
                const range = IDBKeyRange.upperBound(now);
                const request = index.openCursor(range);
                let deletedCount = 0;

                request.onsuccess = (event) => {
                    const cursor = (event.target as IDBRequest).result;
                    if (cursor) {
                        cursor.delete();
                        deletedCount++;
                        cursor.continue();
                    } else {
                        observer.next(deletedCount);
                        observer.complete();
                    }
                };

                request.onerror = () => {
                    observer.error(new Error(`Failed to clear expired entries: ${request.error}`));
                };
            }))
        );
    }

    /**
     * Clear all cache entries
     */
    clear(): Observable<void> {
        return this.ensureInitialized().pipe(
            switchMap((db) => new Observable<void>(observer => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);
                const request = store.clear();

                request.onsuccess = () => {
                    observer.next();
                    observer.complete();
                };
                request.onerror = () => {
                    observer.error(new Error(`Failed to clear cache: ${request.error}`));
                };
            }))
        );
    }

    /**
     * Get the number of entries in cache
     */
    count(): Observable<number> {
        return this.ensureInitialized().pipe(
            switchMap((db) => new Observable<number>(observer => {
                const transaction = db.transaction([this.storeName], 'readonly');
                const store = transaction.objectStore(this.storeName);
                const request = store.count();

                request.onsuccess = () => {
                    observer.next(request.result);
                    observer.complete();
                };
                request.onerror = () => {
                    observer.error(new Error(`Failed to count cache entries: ${request.error}`));
                };
            }))
        );
    }

    /**
     * Get cache statistics
     */
    getStats(): Observable<{
        totalEntries: number;
        expiredEntries: number;
        totalSize: number;
        oldestEntry: number | null;
        newestEntry: number | null;
    }> {
        return this.getAll().pipe(
            map(entries => {
                const now = Date.now();

                let expiredCount = 0;
                let oldestTimestamp: number | null = null;
                let newestTimestamp: number | null = null;
                let totalSize = 0;

                for (const entry of entries) {
                    if (entry.expiresAt && entry.expiresAt < now) {
                        expiredCount++;
                    }

                    if (oldestTimestamp === null || entry.timestamp < oldestTimestamp) {
                        oldestTimestamp = entry.timestamp;
                    }

                    if (newestTimestamp === null || entry.timestamp > newestTimestamp) {
                        newestTimestamp = entry.timestamp;
                    }

                    // Rough size estimation
                    totalSize += JSON.stringify(entry).length;
                }

                return {
                    totalEntries: entries.length,
                    expiredEntries: expiredCount,
                    totalSize,
                    oldestEntry: oldestTimestamp,
                    newestEntry: newestTimestamp
                };
            })
        );
    }

    /**
     * Close the database connection
     */
    close(): void {
        if (this.db) {
            this.db.close();
            this.db = null;
        }
    }

    /**
     * Delete the entire database
     */
    deleteDatabase(): Observable<void> {
        this.close();

        return new Observable<void>(observer => {
            const request = indexedDB.deleteDatabase(this.dbName);

            request.onsuccess = () => {
                observer.next();
                observer.complete();
            };
            request.onerror = () => {
                observer.error(new Error(`Failed to delete database: ${request.error}`));
            };
            request.onblocked = () => {
                console.warn('Database deletion blocked. Close all connections to the database.');
            };
        });
    }
}
